/**
 * Phone number normalisation shared by every write path (guest form, Excel import, user profile).
 *
 * Eventor stores phone numbers in E.164 (`+41791234567`) so that a number entered on one platform
 * is readable on the others. The iOS app (`String.normalizedPhoneNumber`, PhoneNumberKit) and the
 * Android app (libphonenumber) apply the same three steps:
 *   1. already valid E.164 -> keep as is,
 *   2. leading `00` international prefix -> replace with `+`,
 *   3. otherwise parse as a national number using a default region.
 * Keep the behaviour of the three implementations in sync when changing this file.
 */
import { parsePhoneNumberFromString, isValidPhoneNumber } from 'libphonenumber-js';

const E164_NEEDLE = /^\+[1-9]\d{6,14}$/;
const MIN_DIGIT_COUNT = 4;

export function digitCount(value) {
  return (`${value ?? ''}`.match(/\d/g) || []).length;
}

/**
 * Best guess of the region a national number should be parsed with, e.g. `CH`.
 * Mirrors `Locale.current.regionCode` on iOS.
 */
export function defaultPhoneCountry() {
  const locales = (typeof navigator !== 'undefined'
    && (navigator.languages || (navigator.language && [navigator.language]))) || [];

  for (let i = 0; i < locales.length; i += 1) {
    const region = `${locales[i]}`.split(/[-_]/)[1];

    if (region && /^[A-Za-z]{2}$/.test(region)) return region.toUpperCase();
  }

  return undefined;
}

/**
 * Returns the number in E.164, or `null` when the input cannot be understood as a phone number.
 * @param {string} value raw user input
 * @param {string} [country] ISO 3166-1 alpha-2 region used for national numbers
 */
export function normalizePhoneNumber(value, country = defaultPhoneCountry()) {
  const trimmed = `${value ?? ''}`.trim();

  if (trimmed === '') return null;

  const compact = trimmed.replace(/[\s./()-]/g, '');

  if (E164_NEEDLE.test(compact) && isValidPhoneNumber(compact)) return compact;

  const candidates = [trimmed];

  if (/^00\d/.test(compact)) candidates.push(`+${compact.slice(2)}`);

  for (let i = 0; i < candidates.length; i += 1) {
    const parsed = parsePhoneNumberFromString(candidates[i], country);

    if (parsed && parsed.isValid()) return parsed.number;
  }

  // Valid E.164 shape that libphonenumber does not know a rule for (rare ranges) is still useful.
  if (E164_NEEDLE.test(compact)) return compact;

  return null;
}

/**
 * Normalises when possible and otherwise keeps the raw input, so that manual entry never loses data.
 * Values with fewer than four digits are treated as not-a-phone-number and dropped.
 * @returns {string|null}
 */
export function normalizePhoneNumberOrKeep(value, country = defaultPhoneCountry()) {
  const trimmed = `${value ?? ''}`.trim();

  if (trimmed === '') return null;

  const normalized = normalizePhoneNumber(trimmed, country);

  if (normalized) return normalized;
  if (digitCount(trimmed) < MIN_DIGIT_COUNT) return null;

  return trimmed;
}

export function phoneNumberIsAcceptable(value, country = defaultPhoneCountry()) {
  const trimmed = `${value ?? ''}`.trim();

  if (trimmed === '') return true;

  return normalizePhoneNumber(trimmed, country) !== null;
}

export default normalizePhoneNumber;
